// admin/src/pages/Dashboard/components/IntersectionSection.js
import React, { useState, useEffect, useRef } from "react";

/**
 * Wraps lower dashboard sections to trigger data fetching only when scrolled into view.
 * Gracefully falls back to immediately visible if IntersectionObserver is unavailable (e.g., in Jest).
 */
const IntersectionSection = ({ children, onVisible, minHeight = 320, rootMargin = "200px" }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;

  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setIsVisible(true);
      if (onVisibleRef.current) onVisibleRef.current();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (onVisibleRef.current) onVisibleRef.current();
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    const el = containerRef.current;
    if (el) observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin]);

  return (
    <div ref={containerRef} style={{ minHeight: isVisible ? undefined : minHeight, width: "100%" }}>
      {children(isVisible)}
    </div>
  );
};

export default React.memo(IntersectionSection);
